import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Grid, AppBar, Toolbar, Paper,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  InputLabel,
  Modal,
  Select,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
} from '@mui/material';
import { useParams } from 'react-router-dom';
import { Navigate, useNavigate } from 'react-router-dom';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CircularProgress from '@mui/material/CircularProgress';

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

// ── Edit Employee User Guide Steps ────────────────────────────────────────
const editEmployeeGuideData = [
  {
    section: "🖼️ Profile Image",
    steps: [
      {
        label: "Upload Image",
        desc: "Click 'Choose File' to select an image from your device, then click the 'Upload Image' button to save it to the server. The image path is stored automatically after a successful upload.",
      },
      {
        label: "Image Format",
        desc: "Upload a clear passport-size or profile photo of the employee. Supported formats depend on server configuration (typically JPG, PNG).",
      },
    ],
  },
  {
    section: "📝 Employee Basic Details",
    steps: [
      {
        label: "Employee Code",
        desc: "Auto-filled and read-only. This is the unique identifier assigned to the employee and cannot be changed from this form.",
      },
      {
        label: "Employee Name",
        desc: "Full legal name of the employee. This field is mandatory. An error is shown if left empty on save.",
      },
      {
        label: "Date of Birth",
        desc: "Select the employee's date of birth using the date picker. Stored in DD/MM/YYYY format on the server.",
      },
      {
        label: "Date of Joining",
        desc: "Mandatory field. Select the official date the employee joined the organization. An error is shown if left empty on save.",
      },
      {
        label: "Gender",
        desc: "Select Male or Female using the radio buttons. This field is mandatory. An error is shown if no option is selected on save.",
      },
      {
        label: "Mobile No.",
        desc: "Primary contact number of the employee. Mandatory. Limited to 10 digits. An error is shown if left empty on save.",
      },
      {
        label: "Phone (Alternate Mobile)",
        desc: "Optional alternate contact number. Limited to 10 digits. Not required for saving.",
      },
      {
        label: "Email",
        desc: "Employee's email address. Optional but must follow valid email format (e.g. name@domain.com) if entered.",
      },
    ],
  },
  {
    section: "🌍 Location Details",
    steps: [
      {
        label: "Country",
        desc: "Mandatory. Select the employee's country from the dropdown. Selecting a country automatically loads the relevant States in the State dropdown.",
      },
      {
        label: "State",
        desc: "Mandatory. Populated based on the selected Country. Select the appropriate state. Selecting a State loads relevant Cities.",
      },
      {
        label: "City",
        desc: "Mandatory. Populated based on the selected State and Country. Select the employee's city of residence.",
      },
    ],
  },
  {
    section: "🏢 Job Details",
    steps: [
      {
        label: "Department",
        desc: "Mandatory. Select the department to which the employee belongs (e.g. Teaching, Admin, Accounts). Loaded from the Department master.",
      },
      {
        label: "Designation",
        desc: "Mandatory. Select the employee's job title/role (e.g. Teacher, Principal, Clerk). Loaded from the Designation master.",
      },
      {
        label: "Marital Status",
        desc: "Mandatory. Select from Single, Married, or Divorced. An error is shown if not selected on save.",
      },
    ],
  },
  {
    section: "📄 Resume Upload",
    steps: [
      {
        label: "Upload Resume",
        desc: "Click 'Choose File' to select the employee's resume file, then click 'Upload Resume' to save it to the server. The resume path is stored automatically after a successful upload.",
      },
      {
        label: "Resume Format",
        desc: "Typically PDF or DOC files. Supported formats depend on server configuration.",
      },
    ],
  },
  {
    section: "💾 Save & Cancel",
    steps: [
      {
        label: "Save Button",
        desc: "Validates all mandatory fields before submitting. If any required field is missing, a 'Missing Fields' modal lists all the fields that need to be filled. On success, a confirmation modal appears and you are redirected back.",
      },
      {
        label: "Missing Fields Modal",
        desc: "Appears when mandatory fields are empty on save. Lists every missing field by name. Click 'OK' to close and fill the highlighted fields.",
      },
      {
        label: "Success Modal",
        desc: "Appears after a successful update. Click 'OK' to close the modal and navigate back to the previous page automatically.",
      },
      {
        label: "Cancel Button",
        desc: "Discards any unsaved changes and navigates back to the previous page (Employee list) without saving.",
      },
    ],
  },
  {
    section: "🔔 Notifications",
    steps: [
      {
        label: "Success Snackbar (Green)",
        desc: "Shown when image or resume is uploaded successfully.",
      },
      {
        label: "Warning Snackbar (Orange)",
        desc: "Shown when employee details already exist in the system (duplicate record detected).",
      },
      {
        label: "Error Snackbar (Red)",
        desc: "Shown when image/resume upload fails, API errors occur, or the employee update fails for an unexpected reason.",
      },
    ],
  },
];

// ── Edit Employee User Guide Modal ────────────────────────────────────────
const EditEmployeeUserGuide = ({ open, onClose }) => (
  <Dialog
    open={open}
    onClose={onClose}
    maxWidth="sm"
    fullWidth
    PaperProps={{
      sx: {
        borderRadius: "14px",
        overflow: "hidden",
        boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
      },
    }}
  >
    {/* ── Gradient header matching AppBar theme ── */}
    <DialogTitle
      sx={{
        background: "linear-gradient(135deg, #0B1F3D 0%, #1565c0 60%, #26c6da 100%)",
        color: "#fff",
        fontWeight: 700,
        fontSize: "1.1rem",
        textAlign: "center",
        py: 2.5,
        letterSpacing: "0.04em",
      }}
    >
      ✏️ Edit Employee — User Guide
    </DialogTitle>

    {/* ── Scrollable content ── */}
    <DialogContent
      dividers
      sx={{
        px: 3,
        py: 2.5,
        maxHeight: "62vh",
        overflowY: "auto",
        backgroundColor: "#fafafa",
      }}
    >
      {editEmployeeGuideData.map((group, gi) => (
        <Box key={gi} sx={{ mb: gi < editEmployeeGuideData.length - 1 ? 3 : 0 }}>

          {/* Section heading */}
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "0.95rem",
              color: "#1565c0",
              mb: 1.5,
              pb: 0.5,
              borderBottom: "2px solid #bbdefb",
            }}
          >
            {group.section}
          </Typography>

          {/* Step list */}
          {group.steps.map((step, si) => (
            <Box
              key={si}
              sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 1.2 }}
            >
              {/* Bullet dot */}
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  backgroundColor: "#1976d2",
                  mt: "5px",
                  flexShrink: 0,
                }}
              />
              <Typography sx={{ fontSize: "0.875rem", color: "#333", lineHeight: 1.6 }}>
                <Box component="span" sx={{ fontWeight: 700, color: "#111" }}>
                  {step.label}
                </Box>
                {" — "}
                {step.desc}
              </Typography>
            </Box>
          ))}
        </Box>
      ))}

      {/* ── Bottom tip box ── */}
      <Box
        sx={{
          mt: 2.5,
          p: 1.5,
          borderRadius: "8px",
          backgroundColor: "#e3f2fd",
          border: "1px solid #90caf9",
        }}
      >
        <Typography sx={{ fontSize: "0.8rem", color: "#1565c0", fontStyle: "italic" }}>
          💡 <strong>Tip:</strong> Upload the <strong>Image</strong> and <strong>Resume</strong> before clicking <strong>Save</strong> to ensure all employee documents are saved together. Mandatory fields are marked with an error highlight if skipped.
        </Typography>
      </Box>
    </DialogContent>

    {/* ── Close button ── */}
    <DialogActions sx={{ justifyContent: "center", py: 2, backgroundColor: "#fafafa" }}>
      <Button
        onClick={onClose}
        variant="contained"
        sx={{
          background: "linear-gradient(45deg, #0B1F3D 30%, #1976d2 90%)",
          borderRadius: "25px",
          px: 5,
          py: 1,
          fontWeight: 600,
          textTransform: "none",
          fontSize: "0.9rem",
          boxShadow: "0 3px 8px rgba(11,31,61,0.35)",
          "&:hover": {
            background: "linear-gradient(45deg, #0a1a33 30%, #1565c0 90%)",
          },
        }}
      >
        Close
      </Button>
    </DialogActions>
  </Dialog>
);

const EditEmployee = () => {
  const [isImageUploading, setIsImageUploading] = useState(false);
const [isResumeUploading, setIsResumeUploading] = useState(false);
const [isSaving, setIsSaving] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [nameError, setNameError] = useState(false);
  const [genderError, setGenderError] = useState(false);
  const [maritalStatusError, setMaritalStatusError] = useState(false);
  const [departmentError, setDepartmentError] = useState(false);
  const [designationError, setDesignationError] = useState(false);
  const [mobileError, setMobileError] = useState(false);
  const [countryError, setCountryError] = useState(false);
  const [stateError, setStateError] = useState(false);
  const [cityError, setCityError] = useState(false);
  const [dojError, setDojError] = useState(false);
  const [missingFields, setMissingFields] = useState([]);
  const [showMissingFieldsModal, setShowMissingFieldsModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // Controls the Edit Employee User Guide modal open/close
  const [guideOpen, setGuideOpen] = useState(false);

  let navigate = useNavigate();
  const [employeeCode, setEmployeeCode] = useState('');
  const [employeeName, setEmployeeName] = useState('');
  const [dob, setDob] = useState('');
  const [doj, setDoj] = useState('');
  const [gender, setGender] = useState('');
  const [mobileNo, setMobileNo] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [department, setDepartment] = useState('');
  const [designation, setDesignation] = useState('');
  const [country, setCountry] = useState('');
  const [state, setState] = useState('');
  const [city, setCity] = useState('');
  const [maritalStatus, setMaritalStatus] = useState('');
  const [resume, setResume] = useState(null);
  const [imagePath, setImagePath] = useState('');
  const [resumePath, setResumePath] = useState('');
  const [loadingBarProgress, setLoadingBarProgress] = useState('');
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null); // New state to hold the selected image file
  const [selectedResume, setSelectedResume] = useState(null);
  const [employeeData, setEmployeeData] = useState({
    employeeCode: '',
    employeeName: '',
    dob: '',
    doj: '',
    gender: '',
    mobileNo: '',
    alternateMobile: '',
    emailId: '',
    departmentId: '',
    designationId: '',
    countryId: '',
    stateId: '',
    cityId: '',
    maritalStatusId: '',
    resumePath: '',
    imagePath: '',
  });

  const { employeeId } = useParams();

  useEffect(() => {
    const token = sessionStorage.getItem('token');

    const fetchData = async (url, stateSetter, payload = {}) => {
      try {
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: token,
          },
          body: JSON.stringify(payload),
        });
        const data = await response.json();
        if (data && data !== null && data.length > 0) {
          stateSetter(data);
        } else {
          stateSetter([]);
        }
      } catch (error) {
        console.error(`Error fetching data: ${error}`);
      }
    };

    fetchData('https://wsms.aidigitalsec.com/api/Department/DepartmentName', setDepartments);
    fetchData('https://wsms.aidigitalsec.com/api/Designation/DesignationName', setDesignations);

    const fetchCountries = async () => {
      await fetchData('https://wsms.aidigitalsec.com/api/Registration/ddlCountry', setCountries);
    };

    const fetchStates = async () => {
      if (country !== '') {
        await fetchData('https://wsms.aidigitalsec.com/api/Registration/ddlState', setStates, { countryId: country });
      }
    };

    const fetchCities = async () => {
      if (state !== '' && country !== '') {
        await fetchData('https://wsms.aidigitalsec.com/api/Registration/ddlCity', setCities, { stateId: state, countryId: country });
      }
    };

    fetchCountries();
    fetchStates();
    fetchCities();
  }, [country, state]);

  const handleCountryChange = (e) => {
    const selectedCountryId = e.target.value;
    setCountry(selectedCountryId);
    setEmployeeData({ ...employeeData, countryId: selectedCountryId });
  };

  const handleStateChange = (e) => {
    const selectedStateId = e.target.value;
    setState(selectedStateId);
    setEmployeeData({ ...employeeData, stateId: selectedStateId });
  };

  const handleCityChange = (e) => {
    const selectedCityId = e.target.value;
    setCity(selectedCityId);
    setEmployeeData({ ...employeeData, cityId: selectedCityId });
  };

  const handleImageSelect = (e) => {
    setSelectedImage(e.target.files[0]); // Set the selected image file
  };

  const handleImageUpload = async () => {
    setIsImageUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedImage); // Append the selected image file to the FormData

      const token = sessionStorage.getItem('token');
      const response = await fetch('https://wsms.aidigitalsec.com/api/Employee/EmployeeImageUpload', {
        method: 'POST',
        headers: {
          Authorization: token,
        },
        body: formData,
      });

      if (response.ok) {
        const responseData = await response.json();
        setImagePath(responseData.filePath);
        // alert('Image uploaded successfully.');
        setSnackbarMessage('Image uploaded successfully.');
        setOpenSnackbar(true);
        setSnackbarSeverity("success");
      } else {
        // alert('Failed to upload image.');
        setSnackbarMessage('Failed to upload image.');
        setOpenSnackbar(true);
        setSnackbarSeverity("error");
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      // alert('An error occurred while uploading image. Please try again later.');
      setSnackbarMessage('An error occurred while uploading image. Please try again later.');
      setOpenSnackbar(true);
      setSnackbarSeverity("error");
    }finally {
    setIsImageUploading(false);
  }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // previous function
  const handleSave = async (e) => {
    e.preventDefault();

    // Reset error states
    setNameError(false);
    setGenderError(false);
    setMaritalStatusError(false);
    setDepartmentError(false);
    setDesignationError(false);
    setMobileError(false);
    setCountryError(false);
    setStateError(false);
    setCityError(false);
    setDojError(false);

    const missingFields = [];

    if (!employeeData.employeeName) {
      setNameError(true);
      missingFields.push("Employee Name");
    }
    if (!employeeData.gender) {
      setGenderError(true);
      missingFields.push("Gender");
    }
    if (!employeeData.maritalStatusId) {
      setMaritalStatusError(true);
      missingFields.push("Marital Status");
    }
    if (!employeeData.departmentId) {
      setDepartmentError(true);
      missingFields.push("Department");
    }
    if (!employeeData.designationId) {
      setDesignationError(true);
      missingFields.push("Designation");
    }
    if (!employeeData.mobileNo) {
      setMobileError(true);
      missingFields.push("Mobile No.");
    }
    if (!employeeData.countryId) {
      setCountryError(true);
      missingFields.push("Country");
    }
    if (!employeeData.stateId) {
      setStateError(true);
      missingFields.push("State");
    }
    if (!employeeData.cityId) {
      setCityError(true);
      missingFields.push("City");
    }
    if (!employeeData.doj) {
      setDojError(true);
      missingFields.push("Date of Joining");
    }

    if (missingFields.length > 0) {
      setMissingFields(missingFields);
      setShowMissingFieldsModal(true);
      return;
    }

    // // Check if any mandatory field is empty
    // if (
    //   !employeeData.employeeCode ||
    //   !employeeData.employeeName ||
    //   !employeeData.dob ||
    //   !employeeData.doj ||
    //   !employeeData.gender ||
    //   !employeeData.mobileNo ||
    //   !employeeData.emailId ||
    //   // !employeeData.countryId ||
    //   // !employeeData.stateId ||
    //   // !employeeData.cityId ||
    //   !employeeData.departmentId ||
    //   !employeeData.designationId ||
    //   !employeeData.maritalStatusId ||
    //   !imagePath ||
    //   !resumePath
    // ) {
    //   alert('All fields are mandatory.');
    //   return;
    // }

    setIsSaving(true);

    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const response = await fetch(`${apiUrl}/Employee/Id?EmployeeId=${employeeId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: sessionStorage.getItem('token'),
        },
        body: JSON.stringify({
          employeeCode: employeeData.employeeCode,
          employeeName: employeeData.employeeName,
          dob: formatDate(employeeData.dob),
          dateOfJoining: formatDate(employeeData.doj),
          gender: employeeData.gender,
          phone: employeeData.alternateMobile,
          mobile: employeeData.mobileNo,
          emailId: employeeData.emailId,
          counrtyId: country || employeeData.countryId || null,
          stateId: state || employeeData.stateId || null,
          cityId: city || employeeData.cityId || null,
          departmentId: employeeData.departmentId,
          designationId: employeeData.designationId,
          maritalstatusId: employeeData.maritalStatusId,
          imageUrl: imagePath,
          resumePath: resumePath,
        }),
      });

      if (response.ok) {
        const responseData = await response.json();
        setShowSuccessModal(true);

        // Clear all fields upon successful save
        // setEmployeeData('');
        // setResume(null);
      } else {
        const responseData = await response.json();
        if (responseData.status === 'Failed!' && responseData.msg === 'Details already exist!') {
          // alert('Details already exist!');
          setSnackbarMessage('Details already exist!');
          setOpenSnackbar(true);
          setSnackbarSeverity("warning");
        } else {
          // alert('Unable to update employee');
          setSnackbarMessage('Unable to update employee');
          setOpenSnackbar(true);
          setSnackbarSeverity("error");
        }
      }
    } catch (error) {
      console.error('API request error:', error);
      // alert('An error occurred. Please try again later.');
      setSnackbarMessage('An error occurred. Please try again later.');
      setOpenSnackbar(true);
      setSnackbarSeverity("error");
    } finally {
    setIsSaving(false);
  }
  };

  const emailIsValid = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleResumeSelect = (e) => {
    setSelectedResume(e.target.files[0]); // Set the selected image file
  };

  const handleResumeUpload = async () => {
    setIsResumeUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedResume); // Append the selected image file to the FormData

      const token = sessionStorage.getItem('token');
      const response = await fetch('https://wsms.aidigitalsec.com/api/Employee/EmployeeImageUpload', {
        method: 'POST',
        headers: {
          Authorization: token,
        },
        body: formData,
      });

      if (response.ok) {
        const responseData = await response.json();
        setResumePath(responseData.filePath);
        // alert('Resume uploaded successfully.');
        setSnackbarMessage('Resume uploaded successfully.');
        setOpenSnackbar(true);
        setSnackbarSeverity("success");
      } else {
        // alert('Resume to upload image.');
        setSnackbarMessage('Failed to Upload Resume!');
        setOpenSnackbar(true);
        setSnackbarSeverity("error");
      }
    } catch (error) {
      console.error('Error uploading Resume:', error);
      // alert('An error occurred while uploading Resume. Please try again later.');
      setSnackbarMessage('An error occurred while uploading Resume. Please try again later.');
      setOpenSnackbar(true);
      setSnackbarSeverity("error");
    }finally {
    setIsResumeUploading(false);
  }
  };

  const handleCancel = () => {
    // setEmployeeCode('');
    // setEmployeeName('');
    // setDob('');
    // setDoj('');
    // setGender('');
    // setMobileNo('');
    // setPhone('');
    // setEmail('');
    // setDepartment('');
    // setDesignation('');
    // setCountry('');
    // setState('');
    // setCity('');
    // setMaritalStatus('');
    // setResume(null);
    navigate(-1);
  };

  const formatDateForInput = (dateString) => {
    const [day, month, year] = dateString.split('/');
    return `${year}-${month}-${day}`;
  };

  const fetchEmploymeeById = async () => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("token");
      const response = await fetch(`${apiUrl}/Employee/EmpbyId?Id=${employeeId}`, {
        method: "POST",
        headers: {
          "content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({}),
      });

      if (response.ok) {
        const [employee] = await response.json();
        employee.dob = formatDateForInput(employee.dob);
        employee.doj = formatDateForInput(employee.joinDate);
        setEmployeeData(employee);
        setGender(employee.gender);
        setCountry(employee.countryId);
        setState(employee.stateId);
        setCity(employee.cityId);
      } else {
        console.error("Failed to fetch employee data");
      }
    } catch (error) {
      console.error("API request error:", error);
    }
  };

  useEffect(() => {
    fetchEmploymeeById();
  }, []);

  return (
    <Container mt={4}>

      {/* ── Edit Employee User Guide Modal ── */}
      <EditEmployeeUserGuide open={guideOpen} onClose={() => setGuideOpen(false)} />

      <Box
        component="form"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
        }}
      >
        <AppBar position="static" style={{ backgroundColor: "#0B1F3D" }}>
          <Toolbar sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>

            {/* Page title */}
            <Typography variant="h4" component="div" style={{ fontWeight: 'bold' }}>
              Edit Employee
            </Typography>

            {/* ── ℹ️ User Guide Button — top-right of AppBar ── */}
            <Tooltip title="View User Guide" placement="bottom" arrow>
              <IconButton
                onClick={() => setGuideOpen(true)}
                size="small"
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: "50%",
                  backgroundColor: "rgba(255,255,255,0.15)",
                  border: "2px solid rgba(255,255,255,0.7)",
                  color: "#fff",
                  fontWeight: 900,
                  fontSize: "1rem",
                  fontFamily: "serif",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.3)",
                    border: "2px solid #fff",
                    transform: "scale(1.12)",
                    boxShadow: "0 0 12px rgba(255,255,255,0.4)",
                  },
                }}
              >
                i
              </IconButton>
            </Tooltip>

          </Toolbar>
        </AppBar>

        <Paper elevation={3} sx={{ padding: 2, width: '100%', margin: 'auto', marginTop: 1 }}>

          {/* Image Upload */}
       <Grid item xs={12} md={6} sx={{ mb: 3 }}>
  <Typography variant="subtitle2" sx={{ mb: 1, color: '#555' }}>
    Profile Image
  </Typography>
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
    <Button
      component="label"
      variant="outlined"
      size="small"
      startIcon={<CloudUploadIcon />}
      sx={{
        textTransform: 'none',
        borderRadius: '8px',
        borderColor: '#0B1F3D',
        color: '#0B1F3D',
        px: 2,
        '&:hover': { borderColor: '#0B1F3D', backgroundColor: 'rgba(11,31,61,0.04)' },
      }}
    >
      Choose File
      <input type="file" hidden onChange={handleImageSelect} />
    </Button>
    <Typography variant="body2" sx={{ color: '#666', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
      {selectedImage ? selectedImage.name : 'No file chosen'}
    </Typography>
    <Button
  variant="contained"
  size="small"
  onClick={handleImageUpload}
  disabled={!selectedImage || isImageUploading}
  startIcon={isImageUploading ? <CircularProgress size={16} color="inherit" /> : null}
  sx={{ textTransform: 'none', borderRadius: '8px', backgroundColor: '#0B1F3D', boxShadow: 'none', px: 2, '&:hover': { backgroundColor: '#0a1a33', boxShadow: 'none' } }}
>
  {isImageUploading ? 'Uploading...' : 'Upload Image'}
</Button>
  </Box>
</Grid>

          <Grid container item spacing={3} sx={{ width: '100%' }}>
            {/* Employee Code */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Employee Code"
                value={employeeData.employeeCode}
                readOnly // Read-only field
              />
            </Grid>

            {/* Employee Name */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Employee Name"
                value={employeeData.employeeName}
                onChange={(e) => setEmployeeData({ ...employeeData, employeeName: e.target.value })}
              />
            </Grid>

            {/* Date of Birth */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Date of Birth"
                type="date"
                value={employeeData.dob}
                onChange={(e) => setEmployeeData({ ...employeeData, dob: e.target.value })}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>

            {/* Date of Joining */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Date of Joining"
                type="date"
                value={employeeData.doj}
                onChange={(e) => setEmployeeData({ ...employeeData, doj: e.target.value })}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>

            {/* Gender */}
            <Grid item xs={12}>
              <FormControl fullWidth component="fieldset">
                <InputLabel>Gender</InputLabel>
                <RadioGroup
                  row
                  value={employeeData.gender}
                  onChange={(e) => setEmployeeData({ ...employeeData, gender: e.target.value })}
                  style={{ marginTop: '30px' }}
                >
                  <FormControlLabel
                    value="male" // change from "Male"
                    control={<Radio />}
                    label="Male"
                    style={{ marginLeft: '10px' }}
                  />
                  <FormControlLabel
                    value="femal" // change from "Female"
                    control={<Radio />}
                    label="Female"
                  />
                </RadioGroup>
              </FormControl>
            </Grid>

            {/* Mobile No. */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Mobile No."
                value={employeeData.mobileNo}
                onChange={(e) => setEmployeeData({ ...employeeData, mobileNo: e.target.value })}
                inputProps={{ maxLength: 10 }}
              />
            </Grid>

            {/* Phone */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Phone"
                value={employeeData.alternateMobile}
                onChange={(e) => setEmployeeData({ ...employeeData, alternateMobile: e.target.value })}
                inputProps={{ maxLength: 10 }}
              />
            </Grid>

            {/* Email */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={employeeData.emailId}
                onChange={(e) => setEmployeeData({ ...employeeData, emailId: e.target.value })}
              />
            </Grid>

            {/* Country */}
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Country</InputLabel>

                {/* PREVVIOUS CODE */}
                <Select
                  label="Country" value={country || ''} onChange={handleCountryChange}>
                  {countries.map((country) => (
                    <MenuItem key={country.countryId} value={country.countryId}>
                      {country.countryName}
                    </MenuItem>
                  ))}
                </Select>
                {/* PREVVIOUS CODE */}

              </FormControl>
            </Grid>

            {/* State */}
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>State</InputLabel>
                <Select label="State" value={state || ''} onChange={handleStateChange}>
                  {states.map((state) => (
                    <MenuItem key={state.stateId} value={state.stateId}>
                      {state.stateName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* City */}
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>City</InputLabel>
                <Select label="City" value={city || ''} onChange={handleCityChange}>
                  {cities.map((city) => (
                    <MenuItem key={city.cityId} value={city.cityId}>
                      {city.cityName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Department */}
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Department</InputLabel>
                <Select
                  label="Department"
                  value={employeeData.departmentId}
                    onChange={(e) =>
    setEmployeeData({ ...employeeData, departmentId: e.target.value, designationId: '' })
  }
                >
                  {departments.map((department) => (
                    <MenuItem key={department.deptId} value={department.deptId}>
                      {department.departmentName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Designation */}
            <Grid item xs={12}>
              <FormControl fullWidth>
  <InputLabel>Designation</InputLabel>
  <Select
    label="Designation"
    value={employeeData.designationId}
    disabled={!employeeData.departmentId}
    onChange={(e) => setEmployeeData({ ...employeeData, designationId: e.target.value })}
  >
    {designations.filter((d) => String(d.departmentId) === String(employeeData.departmentId)).length === 0 ? (
      <MenuItem disabled value="">
        No designations available
      </MenuItem>
    ) : (
      designations
        .filter((d) => String(d.departmentId) === String(employeeData.departmentId))
        .map((designation) => (
          <MenuItem key={designation.desigId} value={designation.desigId}>
            {designation.designationName}
          </MenuItem>
        ))
    )}
  </Select>
</FormControl>
            </Grid>

            {/* Marital Status */}
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Marital Status</InputLabel>
                <Select
                  label="Marital Status"
                  value={employeeData.maritalStatusId}
                  onChange={(e) => setEmployeeData({ ...employeeData, maritalStatusId: e.target.value })}
                >
                  <MenuItem value="1">Single</MenuItem>
                  <MenuItem value="2">Married</MenuItem>
                  <MenuItem value="3">Divorced</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Upload Resume */}
           <Grid item xs={12} sx={{ mb: 3 }}>
  <Typography variant="subtitle2" sx={{ mb: 1, color: '#555' }}>
    Resume
  </Typography>
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
    <Button
      component="label"
      variant="outlined"
      size="small"
      startIcon={<CloudUploadIcon />}
      sx={{
        textTransform: 'none',
        borderRadius: '8px',
        borderColor: '#0B1F3D',
        color: '#0B1F3D',
        px: 2,
        '&:hover': { borderColor: '#0B1F3D', backgroundColor: 'rgba(11,31,61,0.04)' },
      }}
    >
      Choose File
      <input type="file" hidden onChange={handleResumeSelect} />
    </Button>
    <Typography variant="body2" sx={{ color: '#666', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
      {selectedResume ? selectedResume.name : 'No file chosen'}
    </Typography>
  <Button
  variant="contained"
  size="small"
  onClick={handleResumeUpload}
  disabled={!selectedResume || isResumeUploading}
  startIcon={isResumeUploading ? <CircularProgress size={16} color="inherit" /> : null}
  sx={{ textTransform: 'none', borderRadius: '8px', backgroundColor: '#0B1F3D', boxShadow: 'none', px: 2, '&:hover': { backgroundColor: '#0a1a33', boxShadow: 'none' } }}
>
  {isResumeUploading ? 'Uploading...' : 'Upload Resume'}
</Button>
  </Box>
</Grid>
          </Grid>

          {/* Save and Cancel Buttons */}
          <Grid item xs={12} sx={{ mt: 3 }}>
          <Button
  variant="contained"
  color="primary"
  onClick={handleSave}
  disabled={isSaving}
  startIcon={isSaving ? <CircularProgress size={18} color="inherit" /> : null}
  sx={{ mr: 2 }}
>
  {isSaving ? 'Saving...' : 'Save'}
</Button>
            <Button variant="contained" color="error" onClick={handleCancel} sx={{ ml: 2 }}>
              Cancel
            </Button>
          </Grid>
        </Paper>
      </Box>

      {/* ── Success Modal ── */}
      <Modal
        open={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        aria-labelledby="success-modal-title"
        aria-describedby="success-modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <Box sx={{ backgroundColor: '#0B1F40', padding: '12px 16px' }}>
            <Typography id="success-modal-title" variant="h6" sx={{ color: '#fff' }}>
              Employee Updated ✅
            </Typography>
          </Box>

          {/* Content */}
          <Box sx={{ padding: '20px' }}>
            <Typography id="success-modal-description" sx={{ mb: 2 }}>
              Employee Data is Updated Successfully.
            </Typography>

            {/* Buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button
                onClick={() => {
                  setShowSuccessModal(false);
                  navigate(-1); // go back after closing modal
                }}
                variant="contained"
                color="primary"
              >
                OK
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* ── Missing Fields Modal ── */}
      <Modal
        open={showMissingFieldsModal}
        onClose={() => setShowMissingFieldsModal(false)}
        aria-labelledby="missing-fields-modal-title"
        aria-describedby="missing-fields-modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <Box sx={{ backgroundColor: '#0B1F40', padding: '12px 16px' }}>
            <Typography id="missing-fields-modal-title" variant="h6" sx={{ color: '#fff' }}>
              Missing Fields
            </Typography>
          </Box>
          {/* Content */}
          <Box sx={{ padding: '20px' }}>
            <Typography id="missing-fields-modal-description" sx={{ mb: 2 }}>
              Please fill in the following required fields:
            </Typography>
            <ul>
              {missingFields.map((field, index) => (
                <li key={index}>- {field}</li>
              ))}
            </ul>
            {/* Buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button
                onClick={() => setShowMissingFieldsModal(false)}
                variant="contained"
                color="primary"
              >
                OK
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

    </Container>
  );
};

export default EditEmployee;